import { useState } from "react";
import { useT } from "@reciperoll/core";
import {
  newMethodRow,
  methodRowsToText,
  textToMethodRows,
  type MethodRow,
} from "../utils/recipeFormRows";
import { SortableList, GripHandle } from "./SortableList";

export type { MethodRow };

const iconBtn =
  "w-7 h-7 shrink-0 flex items-center justify-center rounded-md text-ink-faint hover:text-ink hover:bg-surface-2 disabled:opacity-30 disabled:hover:bg-transparent";
const rowInput =
  "rounded-lg border border-line bg-surface dark:bg-surface-2 px-2.5 py-1.5 text-sm text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta";

export function RecipeMethodEditor({
  rows,
  onChange,
}: {
  rows: MethodRow[];
  onChange: (rows: MethodRow[]) => void;
}) {
  const t = useT();
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const enterBulk = () => {
    setBulkText(methodRowsToText(rows));
    setBulk(true);
  };
  const applyBulk = () => {
    onChange(textToMethodRows(bulkText));
    setBulk(false);
  };

  const patch = (id: string, p: Partial<MethodRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const remove = (id: string) => onChange(rows.filter((r) => r.id !== id));

  // Step numbers, restarting after each heading (matches the recipe view).
  // Precomputed by id so labels are stable regardless of render order on drag.
  const stepLabels: Record<string, string> = {};
  let n = 0;
  for (const row of rows) {
    if (row.isGroup) {
      n = 0;
      stepLabels[row.id] = "";
    } else {
      n += 1;
      stepLabels[row.id] = String(n);
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-1">
        <button
          type="button"
          onClick={bulk ? applyBulk : enterBulk}
          className="text-xs font-semibold text-ink-soft hover:text-ink"
        >
          {bulk ? t("common.done") : t("form.bulkEdit")}
        </button>
      </div>

      {bulk ? (
        <textarea
          className="w-full rounded-lg border border-line bg-surface dark:bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
          rows={12}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={t("form.methodBulkPlaceholder")}
          autoFocus
        />
      ) : (
        <div>
          <SortableList items={rows} onReorder={onChange} className="space-y-2" renderItem={(row, handle) => (
            <div className="flex items-start gap-1.5">
              <GripHandle handle={handle} className="w-6 h-9" />

              {!row.isGroup && (
                <span className="w-6 shrink-0 pt-2 text-sm font-bold text-ink-faint text-right">{stepLabels[row.id]}</span>
              )}

              {row.isGroup ? (
                <input
                  className={`${rowInput} flex-1 font-bold uppercase tracking-wide text-ink-soft`}
                  value={row.text}
                  onChange={(e) => patch(row.id, { text: e.target.value })}
                  placeholder={t("form.sectionHeadingServePlaceholder")}
                />
              ) : (
                <textarea
                  className={`${rowInput} flex-1 resize-y`}
                  rows={2}
                  value={row.text}
                  onChange={(e) => patch(row.id, { text: e.target.value })}
                  placeholder={t("form.describeStep")}
                />
              )}

              <button type="button" className={iconBtn} onClick={() => remove(row.id)} aria-label={t("common.remove")}>✕</button>
            </div>
          )} />

          <div className="flex gap-2 pt-3">
            <button
              type="button"
              onClick={() => onChange([...rows, newMethodRow(false)])}
              className="text-sm font-semibold text-terracotta hover:text-terracotta-dark"
            >
              + {t("form.step")}
            </button>
            <button
              type="button"
              onClick={() => onChange([...rows, newMethodRow(true)])}
              className="text-sm font-semibold text-ink-soft hover:text-ink"
            >
              + {t("form.heading")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
